import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useLoginDialog } from "@/contexts/LoginDialogContext";
import { useSignupDialog } from "@/contexts/SignupDialogContext";
import { useSiteSetting } from "@/hooks/useSiteSettings";

const LoginDialog = () => {
  const { isOpen, closeLoginDialog } = useLoginDialog();
  const { openSignupDialog } = useSignupDialog();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: registrationDisabled } = useSiteSetting("registration_disabled");
  const { data: googleEnabled } = useSiteSetting("auth_google_enabled");
  const { data: appleEnabled } = useSiteSetting("auth_apple_enabled");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-close dialog when user signs in (e.g. via Google OAuth)
  useEffect(() => {
    if (user && isOpen) {
      closeLoginDialog();
      navigate("/feed");
    }
  }, [user, isOpen, closeLoginDialog, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast.error(error.message);
    } else {
      closeLoginDialog();
      setEmail("");
      setPassword("");
      navigate("/feed");
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (error) toast.error("Google sign-in failed");
  };

  const handleAppleLogin = async () => {
    const { error } = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin,
    });
    if (error) toast.error("Apple sign-in failed");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeLoginDialog()}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden rounded-2xl">
        <DialogTitle className="sr-only">Sign In</DialogTitle>
        <div className="p-4 md:p-6 pb-2 text-center">
          <span className="font-display text-xl md:text-2xl font-bold gradient-text">Gloogum</span>
          <p className="text-muted-foreground text-xs md:text-sm mt-1">Welcome back! Sign in to continue.</p>
        </div>

        <div className="px-4 md:px-6 pb-4 md:pb-6 pt-2">
          {(googleEnabled || appleEnabled) && (
          <div className="space-y-2.5 mb-5">
            {googleEnabled && (
            <Button variant="outline" className="w-full h-11 text-sm font-medium" onClick={handleGoogleLogin}>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>
            )}
            {appleEnabled && (
            <Button variant="outline" className="w-full h-11 text-sm font-medium" onClick={handleAppleLogin}>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              Continue with Apple
            </Button>
            )}
          </div>
          )}

          {(googleEnabled || appleEnabled) && (
          <div className="flex items-center gap-3 mb-5">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground uppercase">or</span>
            <Separator className="flex-1" />
          </div>
          )}

          <form onSubmit={handleLogin} className="space-y-3">
            <div>
              <Label htmlFor="dialog-email" className="text-xs">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="dialog-email" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 h-11" required />
              </div>
            </div>
            <div>
              <div className="flex justify-between">
                <Label htmlFor="dialog-password" className="text-xs">Password</Label>
                <Link to="/forgot-password" onClick={closeLoginDialog} className="text-xs text-primary hover:underline">Forgot password?</Link>
              </div>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="dialog-password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 pr-10 h-11" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" variant="hero" className="w-full h-11" disabled={loading}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-5">
            Don't have an account?{" "}
            <button
              onClick={() => { closeLoginDialog(); openSignupDialog(); }}
              disabled={registrationDisabled}
              title={registrationDisabled ? "Registration is temporarily disabled" : undefined}
              className="text-primary font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline"
            >
              Sign up
            </button>
          </p>
          {registrationDisabled && (
            <p className="text-center text-xs text-destructive mt-2">Registration is temporarily disabled.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
